/**
 * The pool Settings Danger zone (ISSUE-817 R5), design prompt
 * `27-pool-settings.md` §27.2 card 10.
 *
 * It carries two actions. `Hand over the pool` moves the role to somebody
 * else and is the original one. `Close this pool` ends a pool nobody else is
 * in, and shipped once `services/pool-api` grew `POST /pool/archive`; before
 * that endpoint existed this card was an honest dead end for a Commissioner
 * who simply wanted the pool gone, and `HandOverPoolDialog`'s sole-member
 * empty state was the end of the road.
 *
 * `thenLeave` is FALSE here, and that is the whole difference between this
 * entry point and the Commissioner's own `Leave pool` row: handing the pool
 * over from Settings transfers the role and the old Commissioner stays in the
 * pool, as an Admin: `transferOwnership` demotes them to `admin` in the same
 * transaction that promotes the successor. Nothing on this path calls `/pool/leave`.
 *
 * Only the Commissioner ever sees this card: `visibleSlotsForRole` drops every
 * `isDanger` slot for an Admin, and both `/ownership/transfer` and
 * `/pool/archive` are owner-only, so an Admin must never be shown a row the
 * server will refuse.
 */
import { Button } from "@sportsmet/ui";
import { type ReactNode } from "react";

import { useOverlay } from "../../routeState/useOverlay";
import {
  HAND_OVER_DIALOG_NAME,
  HandOverPoolDialog,
} from "../poolMembers/HandOverPoolDialog";
import { ClosePoolButton } from "./ClosePoolButton";

export const HAND_OVER_LABEL = "Hand over the pool";
export const HAND_OVER_HINT = "Pick who takes over running this pool.";

export type typePoolSettingsDangerZoneCardProps = {
  poolId: string;
  /**
   * From `POST /pool/get`, forwarded to the close control so it can say why
   * closing is unavailable. Null until that read lands.
   */
  activeMemberCount?: number | null | undefined;
  pendingMemberCount?: number | null | undefined;
};

export const PoolSettingsDangerZoneCard = ({
  poolId,
  activeMemberCount = null,
  pendingMemberCount = null,
}: typePoolSettingsDangerZoneCardProps): ReactNode => {
  const overlay = useOverlay();
  const isOpen = overlay.isOpen("dialog", HAND_OVER_DIALOG_NAME);

  return (
    <div
      className="flex flex-col gap-2"
      data-testid="pool-settings-danger-zone"
    >
      <Button
        variant="destructive"
        size="md"
        data-testid="pool-settings-hand-over"
        onClick={() => {
          overlay.open({ kind: "dialog", name: HAND_OVER_DIALOG_NAME });
        }}
      >
        {HAND_OVER_LABEL}
      </Button>
      {/*
        The hint sits under the control rather than inside a confirm: a confirm
        raised before the picker would name nobody, so the confirm lives on the
        row where the new Commissioner's name is known.
      */}
      <p className="text-caption text-ink-secondary">{HAND_OVER_HINT}</p>

      {/*
        Second, and under a rule of its own: handing over keeps the pool
        alive, closing ends it. They are not two ways to do one thing, so
        they are two rows rather than one control with a choice in it.
      */}
      <ClosePoolButton
        poolId={poolId}
        activeMemberCount={activeMemberCount}
        pendingMemberCount={pendingMemberCount}
      />

      <HandOverPoolDialog
        open={isOpen}
        poolId={poolId}
        thenLeave={false}
        onOpenChange={(next) => {
          if (!next) overlay.close({ kind: "dialog" });
        }}
        onDone={() => {
          // They are still here, now as an ordinary member. The generated hook
          // re-reads `/pool/get`, so the Settings screen loses its Danger zone
          // on its own rather than being navigated away from.
          overlay.close({ kind: "dialog" });
        }}
      />
    </div>
  );
};
